import styled from '@emotion/styled';
import { useLingui } from '@lingui/react/macro';
import { getRegistryEntry } from 'modules/manager/domain/placeholder/placeholder-key-registry';
import { PlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import React, { createContext, useContext } from 'react';
import { theme } from 'styles/theme/theme';
import { editorAccent } from 'modules/manager/ui/components/editor/editor-accent';

interface SchemeState {
  /** Keys the template type allows — a `Hot` for any other key is not drawn. */
  allowed: ReadonlySet<PlaceholderKey>;
  highlightKey?: PlaceholderKey;
}

const SchemeContext = createContext<SchemeState>({ allowed: new Set() });

export const SchemeProvider = SchemeContext.Provider;

interface HotProps {
  k: PlaceholderKey;
  children: React.ReactNode;
  /** Drawn in place of the element when the template type does not allow `k`. */
  fallback?: React.ReactNode;
  className?: string;
}

/**
 * The one place a key lives on a scheme. Each key gets exactly one `Hot` per
 * scheme, so the highlight always lands on a single element; repeated rows
 * around it are plain decoration.
 */
export const Hot: React.FC<HotProps> = ({ k, children, fallback = null, className }) => {
  const { allowed, highlightKey } = useContext(SchemeContext);
  if (!allowed.has(k)) return <>{fallback}</>;
  return (
    <HotBox
      data-key={k}
      data-testid={`scheme-key-${k}`}
      data-highlighted={k === highlightKey ? 'true' : undefined}
      className={className}
    >
      {children}
    </HotBox>
  );
};

/**
 * Scheme texts come from the registry — the same sample the picker list shows,
 * and the examples for the decorative rows — so a scheme needs no copy of its own.
 * `index` 0 is the sample, 1… are the examples after the first.
 */
export function useKeyText(): (key: PlaceholderKey, index?: number) => string {
  const { t } = useLingui();
  return (key, index = 0) => {
    const entry = getRegistryEntry(key);
    const descriptor = index === 0 ? entry?.sample : entry?.examples?.[index];
    return descriptor ? t(descriptor) : '';
  };
}

export const Poster = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: ${theme.radii.card};
  background: ${theme.colors.SurfaceSpaceCadet50};
  color: ${theme.colors.InkOnDark};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
`;

export const HotBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.radii.control};
  outline: 1px dashed ${theme.colors.SurfaceIndependence40};
  outline-offset: 1px;

  &[data-highlighted] {
    outline: 2px solid ${editorAccent.solid};
    background: ${theme.colors.DarkYellowAlpha40};
  }
`;

/** Text that is only there to make the picture readable — never a key. */
export const Decor = styled.span`
  color: ${theme.colors.SurfaceRomanSilver30};
`;

export const PosterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const Titles = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
`;

export const Title = styled.span`
  font-size: 14px;
  font-weight: 700;
`;

export const Subtitle = styled.span`
  font-size: 11px;
`;

export const Logo = styled.span<{ round?: boolean }>`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ round }) => (round ? theme.radii.pill : theme.radii.card)};
  background: ${theme.colors.SurfaceIndependence40};

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const SchemeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;

  th {
    padding: 4px 3px;
    font-size: 10px;
    font-weight: 600;
    text-align: center;
  }

  td {
    padding: 4px 3px;
    text-align: center;
    border-top: 1px solid ${theme.colors.SurfaceIndependence40};
  }

  th.left,
  td.left {
    text-align: left;
  }
`;
